import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import './LandingPage.css'

const LandingPage = () => {
  const heroRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLDivElement>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    businessName: '',
    companyDescription: '',
    service: '',
    budget: '',
    dateTime: ''
  })

  // Slider images array
  const sliderImages = [
    '/images/slider/Slider images-01.png',
    '/images/slider/Slider images-02.png',
    '/images/slider/Slider images-03.png',
    '/images/slider/Slider images-04.png',
    '/images/slider/Slider images-05.png',
    '/images/slider/Slider images-06.png',
    '/images/slider/Slider images-07.png',
    '/images/slider/Slider images-08.png',
    '/images/slider/Slider images-09.png',
  ]


  // Preload all slider images for immediate loading
  useEffect(() => {
    const imagesToPreload = [
      '/images/slider/Slider images-01.png',
      '/images/slider/Slider images-02.png',
      '/images/slider/Slider images-03.png',
      '/images/slider/Slider images-04.png',
      '/images/slider/Slider images-05.png',
      '/images/slider/Slider images-06.png',
      '/images/slider/Slider images-07.png',
      '/images/slider/Slider images-08.png',
      '/images/slider/Slider images-09.png',
    ]
    imagesToPreload.forEach((imageSrc) => {
      const img = new Image()
      img.src = imageSrc
    })
  }, []) // Run once on mount

  // Auto carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length)
    }, 2000) // Change slide every 2 seconds

    return () => clearInterval(interval)
  }, [sliderImages.length])

  const handleBookCall = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!formData.name.trim() || !formData.email.trim() || !formData.location.trim() || 
        !formData.businessName.trim() || !formData.companyDescription.trim() || 
        !formData.service.trim() || !formData.budget || !formData.dateTime) {
      alert('Please fill in all required fields including date and time.')
      return
    }

    // Validate date
    if (!formData.dateTime || isNaN(new Date(formData.dateTime).getTime())) {
      alert('Please select a valid date and time.')
      return
    }

    try {
      // Prepare data for Supabase - matching the simplified schema from setup-database.sql
      // Prepare data for Supabase - matching the simplified schema from setup-database.sql
      // Ensure budget is a proper string value (text type in database)
      const submissionData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        location: formData.location.trim(),
        business_name: formData.businessName.trim(),
        company_description: formData.companyDescription.trim(),
        service_package: formData.service.trim(), // Map 'service' from form to 'service_package' in DB
        budget: formData.budget ? String(formData.budget).trim() : '', // Ensure it's a string
        date_time: new Date(formData.dateTime).toISOString(), // Required field in schema
        referral_source: 'Website Landing Page' // Required field in new schema
      }
      
      // Validate budget is not empty
      if (!submissionData.budget) {
        alert('Please select a budget option.')
        return
      }

      console.log('Submitting form data:', submissionData)
      console.log('Supabase client initialized:', !!supabase)
      
      // Check if Supabase client is properly configured
      if (!supabase) {
        throw new Error('Supabase client is not initialized. Check environment variables.')
      }
      
      // Insert into Supabase with detailed logging
      console.log('Attempting to insert into Supabase...')
      const { data, error } = await supabase
        .from('contact_form_submissions')
        .insert([submissionData])
        .select()
      
      console.log('Insert result - data:', data)
      console.log('Insert result - error:', error)
      if (error) {
        console.log('Error code:', error.code)
        console.log('Error message:', error.message)
        console.log('Error hint:', error.hint)
        console.log('Error details:', error.details)
      }

      if (error) {
        console.error('Supabase error:', error)
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        
        // Provide helpful error messages
        let userFriendlyMessage = 'Failed to submit form. '
        if (error.code === '23505') {
          userFriendlyMessage += 'This email has already been submitted.'
        } else if (error.code === '23502') {
          userFriendlyMessage += 'Missing required information.'
        } else if (error.code === '42501') {
          userFriendlyMessage += 'Database permission error. The form submission is being blocked by security settings. Please refresh the page and try again.'
        } else if (error.message) {
          userFriendlyMessage += error.message
        } else {
          userFriendlyMessage += 'Please try again or contact support if the problem persists.'
        }
        
        alert(`Error: ${userFriendlyMessage}`)
        return
      }

      console.log('Form submitted successfully:', data)

      // Success!
      alert('Thank you! We will contact you soon.')
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        location: '',
        businessName: '',
        companyDescription: '',
        service: '',
        budget: '',
        dateTime: ''
      })
    } catch (error: any) {
      console.error('Error submitting form:', error)
      console.error('Error type:', typeof error)
      console.error('Error name:', error?.name)
      console.error('Error message:', error?.message)
      console.error('Error stack:', error?.stack)
      console.error('Full error object:', JSON.stringify(error, null, 2))
      
      let errorMessage = 'Something went wrong. Please try again later.'
      
      if (error?.message) {
        if (error.message.includes('timeout') || error.message.includes('Timeout')) {
          errorMessage = 'Request timeout: Please check your internet connection and try again.'
        } else if (error.message.includes('Failed to fetch') || error.message.includes('Load failed') || error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
          errorMessage = 'Network error: Please check your internet connection. If the problem persists, please contact us directly. Error code: NETWORK_ERROR'
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Connection error: Please try again or contact support. Error code: CORS_ERROR'
        } else if (error.message.includes('JWT') || error.message.includes('auth')) {
          errorMessage = 'Authentication error: Please refresh the page and try again. Error code: AUTH_ERROR'
        } else {
          errorMessage = `${error.message}`
        }
      } else if (error?.toString) {
        errorMessage = error.toString()
      }
      
      alert(`Error: ${errorMessage}\n\nPlease check the browser console (F12) for more details.`)
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset

      // Parallax effect for hero section
      if (heroRef.current) {
        const heroElements = heroRef.current.querySelectorAll('.parallax-slow')
        heroElements.forEach((el) => {
          const element = el as HTMLElement
          element.style.transform = `translateY(${scrolled * 0.3}px)`
        })
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section ref={heroRef} className="hero-section">
        {/* Logo Header - Full Width */}
        <div className="logo-header parallax-slow">
          <img src="/images/logo.png" alt="BOLD! CREATIVE STUDIO Logo" className="logo-image" />
        </div>

        {/* Hero Content */}
        <div className="hero-content">
          <div className="message-bubble-container parallax-slow">
            <div className="speech-bubble-icon left-bubble">
              <img src="/images/icons BCS-35.svg" alt="Speech bubble" className="bubble-icon" />
            </div>
            <div className="message-bubble">
              <h2 className="main-headline">UNFORGETTABLE BRAND IDENTITIES</h2>
              <p className="sub-headline">for bold businesses that dare to be different</p>
              <p className="description">
                From strategy to social – we craft identities and content that break boundaries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Motto Section */}
      <section className="motto-section">
        <div className="motto-content">
          <p className="motto-text">We've built brands that</p>
          <p className="motto-highlight">DON'T BLEND IN — THEY BREAK THROUGH.</p>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="portfolio-section">
        <div className="portfolio-wavy-top">
          {/* Wave 1 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-1">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
          {/* Wave 2 - White (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white">
            <path d="M0,50 Q60,80 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
          {/* Wave 3 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-2">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
        </div>
        <div className="portfolio-right-icon">
          <img src="/images/icons BCS-16.svg" alt="B Speech bubble" className="portfolio-icon" />
        </div>
        <div className="portfolio-content">
          <div className="portfolio-carousel">
            <div className="carousel-container">
              {(() => {
                // Calculate indices for previous, current, and next with proper wrapping
                const prevIndex = currentSlide === 0 ? sliderImages.length - 1 : currentSlide - 1
                const currentIndex = currentSlide
                const nextIndex = currentSlide === sliderImages.length - 1 ? 0 : currentSlide + 1

                // Create array with only the 3 visible slides
                const visibleSlides = [
                  { index: prevIndex, position: 'left' },
                  { index: currentIndex, position: 'center' },
                  { index: nextIndex, position: 'right' }
                ]

                return visibleSlides.map(({ index, position }) => (
                  <div
                    key={index}
                    className={`carousel-slide ${position}`}
                  >
                    <img src={sliderImages[index]} alt={`Portfolio ${index + 1}`} loading="eager" />
                  </div>
                ))
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-top-text">
          <p>From beauty to tech — every identity we craft tells a story, not just a logo.</p>
        </div>
        <div className="cta-icon-right">
          <img src="/images/icons BCS-17.svg" alt="Monitor icon" className="cta-icon" />
        </div>
        <div className="cta-content">
          <button className="cta-button" onClick={handleBookCall}>BOOK A FREE CALL</button>
          <h2 className="cta-headline">HOW WE BUILD FEARLESS BRANDS</h2>
          <p className="cta-subtext">Let's talk strategy, story, and standout design — all tailored to you.</p>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="services-icon-top-left">
          <img src="/images/icons BCS-23.svg" alt="Decorative icon" className="services-decorative-icon" />
        </div>
        <div className="services-icon-bottom-left">
          <img src="/images/icons BCS-45.svg" alt="Decorative icon" className="services-decorative-icon" />
        </div>
        <div className="services-content">
          <div className="services-grid">
            {/* Service Card 1 */}
            <div className="service-card">
              <div className="service-image">
                <img src="/newimages/Corporate BRANDING.jpg" alt="Corporate Branding" loading="lazy" />
              </div>
              <h3 className="service-title">CORPORATE BRANDING & DESIGN</h3>
            </div>

            {/* Service Card 2 */}
            <div className="service-card">
              <div className="service-image">
                <img src="/newimages/Branding.png" alt="Brand Development" loading="lazy" />
              </div>
              <h3 className="service-title">BRAND DEVELOPMENT & VISUAL IDENTITY</h3>
            </div>

            {/* Service Card 3 */}
            <div className="service-card">
              <div className="service-image">
                <img src="/newimages/packaging cans.png" alt="Packaging Design" loading="lazy" />
              </div>
              <h3 className="service-title">PACKAGING DESIGN</h3>
            </div>

            {/* Service Card 4 */}
            <div className="service-card">
              <div className="service-image">
                <img src="/newimages/Social media 2.png" alt="Social Media Management" loading="lazy" />
              </div>
              <h3 className="service-title">SOCIAL MEDIA MANAGEMENT</h3>
            </div>

            {/* Service Card 5 */}
            <div className="service-card">
              <div className="service-image">
                <img src="/newimages/DVC CONCEPT & STTORYBOARD 2.png" alt="DVC Concept" loading="lazy" />
              </div>
              <h3 className="service-title">DVC CONCEPT & STORYBOARDS</h3>
            </div>

            {/* Service Card 6 */}
            <div className="service-card">
              <div className="service-image">
                <img src="/newimages/packlaging.jpg" alt="PR and Trade Design" loading="lazy" />
              </div>
              <h3 className="service-title">PR AND TRADE DESIGN SERVICES</h3>
            </div>
          </div>
        </div>
        <div className="services-wavy-bottom">
          {/* Wave 1 - Pink (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-pink-1">
            <path d="M0,50 Q60,80 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#F4E2EC" />
          </svg>
          {/* Wave 2 - White (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white-bottom">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
          {/* Wave 3 - Pink (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-pink-2">
            <path d="M0,50 Q60,80 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#F4E2EC" />
          </svg>
        </div>
      </section>

      {/* Bold Effect Section */}
      <section className="bold-effect-section">
        <div className="bold-effect-content">
          <h2 className="bold-effect-headline">Stop blending in — start branding out loud.</h2>
          <p className="bold-effect-body">
            Whether you're launching, rebranding, or scaling your presence — let's build something unforgettable together.
          </p>
          <p className="bold-effect-cta">From Idea → Identity → Impact</p>
          <button className="bold-effect-button" onClick={handleBookCall}>BOOK A FREE CALL</button>
        </div>
        <h2 className="bold-effect-bottom-headline">THE BOLD EFFECT, IN THEIR WORDS.</h2>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-wavy-top">
          {/* Wave 1 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-1">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
          {/* Wave 2 - White (downward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-white">
            <path d="M0,50 Q60,80 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#ffffff" />
          </svg>
          {/* Wave 3 - Green (upward peaks) */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" preserveAspectRatio="none" className="wavy-svg wavy-green-2">
            <path d="M0,50 Q60,20 120,50 T240,50 T360,50 T480,50 T600,50 T720,50 T840,50 T960,50 T1080,50 T1200,50 T1320,50 T1440,50 L1440,100 L0,100 Z" fill="#CDD951" />
          </svg>
        </div>
        <div className="testimonials-icon-left">
          <img src="/images/icons BCS-20.svg" alt="Handshake icon" className="testimonials-icon" />
        </div>
        <div className="testimonials-icon-right">
          <img src="/images/icons BCS-25.svg" alt="Megaphone icon" className="testimonials-icon" />
        </div>
        <div className="testimonials-content">
          <div className="testimonials-grid">
            {/* Testimonial Card 1 */}
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                BOLD Creative is a standout when it comes to custom artwork. Their designs are not only visually stunning but also perfectly aligned with your brand identity. Whether you are looking for sleek, minimal designs or something more vibrant, they have the creativity and expertise to bring your vision to life.
              </p>
              <p className="testimonial-client">NEUVIAN SKINCARE</p>
            </div>

            {/* Testimonial Card 2 */}
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                I love how kind, cooperative, and accessible you all are. Working with you feels so smooth and stress-free, and I truly value the support you provide, not just with social media but even beyond, like helping with my clinic interior decor. It really means a lot!
              </p>
              <p className="testimonial-client">Dr Ayesha Kanwal Skin Aesthetics</p>
            </div>

            {/* Testimonial Card 3 */}
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                I had an absolutely wonderful and flawless experience working with Bold CS. Adore all the brand work, services, Artwork stickers are too cute, and just overall the attention to detail, the flexibility, the communication, the color palette, the fonts everything was so well thought of! Extremely happy - can't wait to work again already on future projects & def recommending to my fellow entrepreneurs:
              </p>
              <p className="testimonial-client">ARTHYMIS</p>
            </div>

            {/* Testimonial Card 4 */}
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                I am thrilled to share my outstanding experience with BOLD, the creative agency that helped elevate my business. Their team is truly talented, hardworking, and dedicated to delivering top-notch results. From designing my business logo to crafting stunning sweet boxes, they did a great job. I highly appreciate their work and wholeheartedly recommend Bold Creative Studio
              </p>
              <p className="testimonial-client">SHIREEN KHAANA</p>
            </div>

            {/* Testimonial Card 5 */}
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                I love how you guys truly understood the essence of our brand and absorbed the core idea with clarity first, then brought the concept to life with amazing designs.
              </p>
              <p className="testimonial-client">ODOR GUARD</p>
            </div>

            {/* Testimonial Card 6 */}
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                We had the pleasure of working with Aatrah to brand and launch our two ventures, Heal Yourself and Grizi, & we couldn't be happier with the results. From the very beginning, Aatrah showed incredible creativity, and excitement for our vision. She not only helped us craft impactful brand identities but also strategized & executed highly effective marketing campaigns.
              </p>
              <p className="testimonial-client">GRIZLI</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form Section */}
      <section className="enquiry-form-section" ref={formRef}>
        <div className="enquiry-form-container">
          <h2 className="enquiry-form-heading">Enquiry Form</h2>
          <form className="enquiry-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Your Full name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Your E-mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">Where are you based? *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="businessName">Your Business Name *</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="companyDescription">What does your company do? What services or products do you sell? Please give as many details as possible. *</label>
              <textarea
                id="companyDescription"
                name="companyDescription"
                value={formData.companyDescription}
                onChange={handleChange}
                required
                rows={5}
              />
            </div>

            <div className="form-group">
              <label htmlFor="service">What Service are you looking for? *</label>
              <input
                type="text"
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="budget-label">What is your budget? *</label>
              <div className="budget-options">
                <div className="budget-option">
                  <input
                    type="radio"
                    id="budget-under-140"
                    name="budget"
                    value="Under 140,000"
                    checked={formData.budget === 'Under 140,000'}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="budget-under-140">&lt;140,000</label>
                </div>
                <div className="budget-option">
                  <input
                    type="radio"
                    id="budget-140-200"
                    name="budget"
                    value="140,000 - 200,000"
                    checked={formData.budget === '140,000 - 200,000'}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="budget-140-200">140,000-200,000</label>
                </div>
                <div className="budget-option">
                  <input
                    type="radio"
                    id="budget-200-300"
                    name="budget"
                    value="200,000 - 300,000"
                    checked={formData.budget === '200,000 - 300,000'}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="budget-200-300">200,000-300,000</label>
                </div>
                <div className="budget-option">
                  <input
                    type="radio"
                    id="budget-over-300"
                    name="budget"
                    value="Over 300,000"
                    checked={formData.budget === 'Over 300,000'}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="budget-over-300">&gt;300,000</label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="dateTime">Book a Date & Time *</label>
              <input
                type="datetime-local"
                id="dateTime"
                name="dateTime"
                value={formData.dateTime}
                onChange={handleChange}
                required
              />
            </div>

            <button type="submit" className="enquiry-submit-button">Submit</button>
          </form>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <div className="final-cta-tagline">
            <p className="final-cta-text">Your Brand Has a Voice.<br />Let's Make It Bold.</p>
            <img src="/images/icons BCS-18.svg" alt="Triangle logo" className="final-cta-icon" />
          </div>
        </div>
      </section>
    </div>
  )
}

export default LandingPage
